/**
 * ContextMenu - Reusable dropdown context menu
 * 
 * Uses fixed positioning (portal-like) to avoid overflow clipping from scrollable containers.
 * Position is calculated from the trigger button's bounding rect.
 */

import React, { useEffect, useRef } from 'react';

export interface MenuAction {
  id: string;
  label: string;
  icon?: string;
  onClick: () => void;
  danger?: boolean;
  warning?: boolean;
  accent?: boolean;
}

interface ContextMenuProps {
  actions: MenuAction[];
  onClose: () => void;
  /** Bounding rect of the trigger button, for positioning */
  anchorRect?: DOMRect | null;
}

export const ContextMenu: React.FC<ContextMenuProps> = ({ actions, onClose, anchorRect }) => {
  const menuRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    // Use setTimeout so the click that opened the menu doesn't immediately close it
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handler);
    }, 0);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handler);
    };
  }, [onClose]);

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  // Calculate position: prefer below-right of anchor, but flip if near viewport edge
  const style: React.CSSProperties = {};
  if (anchorRect) {
    const menuWidth = 160;
    const menuHeight = 200; // approximate
    
    let top = anchorRect.bottom + 4;
    let left = anchorRect.right - menuWidth;
    
    // Flip up if too close to bottom
    if (top + menuHeight > window.innerHeight) {
      top = anchorRect.top - menuHeight - 4;
    }
    // Keep on screen
    if (left < 8) left = 8;
    if (top < 8) top = 8;
    
    style.position = 'fixed';
    style.top = `${top}px`;
    style.left = `${left}px`;
  }

  return (
    <div
      ref={menuRef}
      className="fixed z-[100] w-40 bg-gray-800 border border-gray-700 rounded-lg shadow-xl py-1"
      style={style}
      data-testid="context-menu"
    >
      {actions.map((action) => {
        // Divider
        if (action.id === 'divider') {
          return <div key={`divider-${Math.random()}`} className="border-t border-gray-700 my-1" />;
        }

        let textColor = 'text-gray-300 hover:text-white';
        if (action.danger) textColor = 'text-red-400 hover:text-red-300';
        if (action.warning) textColor = 'text-amber-400 hover:text-amber-300';
        if (action.accent) textColor = 'text-indigo-400 hover:text-indigo-300';

        return (
          <button
            key={action.id}
            className={`w-full px-3 py-1.5 text-left text-xs ${textColor} hover:bg-gray-700/50 transition-colors flex items-center gap-2`}
            onClick={(e) => { e.stopPropagation(); action.onClick(); onClose(); }}
          >
            {action.icon && (
              <span className="text-gray-500 text-[10px] w-3">{action.icon}</span>
            )}
            {action.label}
          </button>
        );
      })}
    </div>
  );
};
